import React from 'react'

import ActionsBox from '@/app/module/campaigns/component/actions/actions-box'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { SMS_ACTION_DEFAULT_SENDERID } from '@/app/module/campaigns/definitions'
import {
  CampaignTemplateType,
  ChoiceValueMultiple,
  Language,
  LocalAction,
  LocalOnAnswerType,
  PartFamily,
  RangeType,
  ReplyType,
  SenderIdOption,
} from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'
import { CountryTimezone, FilesType, PersonalizationType, TimezoneWithCountryCodeType } from '@/app/types'

import ChoiceTagMultiple from './choice-tag-multiple'
import { GetImageProps, GetVoiceProps } from './part-config-props'

type Props = {
  actions: LocalAction[]
  defaultLanguage: string
  files: FilesType
  id: string
  index: number
  isCloseDisabled: boolean
  label: string
  languages: Language[]
  orgId: number
  personalizationList: PersonalizationType
  questionIndex: number
  ranges: RangeType[]
  replies: ReplyType[]
  senderIds: SenderIdOption[]
  token: string
  timezone: string
  timezones: TimezoneWithCountryCodeType
  countryCode: string
  countriesTimezones: CountryTimezone[]
  type: CampaignTemplateType

  getImageProps: GetImageProps
  getVoiceProps: GetVoiceProps
  onChange: (value: Partial<LocalOnAnswerType>, index: number) => void
  onClose: (index: number) => void
}

const ResponseVoiceSpecificItem: React.FC<Props> = ({
  actions,
  countriesTimezones,
  countryCode,
  defaultLanguage,
  files,
  id,
  index,
  isCloseDisabled,
  label,
  languages,
  orgId,
  personalizationList,
  questionIndex,
  ranges,
  replies,
  senderIds,
  timezone,
  timezones,
  token,
  type,
  getImageProps,
  getVoiceProps,
  onChange,
  onClose,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const handleActionsChange = React.useCallback(
    (actionsList: LocalAction[]) => {
      onChange({ actions: actionsList }, index)
    },
    [index, onChange],
  )
  const handleChoiceChange = React.useCallback(
    (newValue: ChoiceValueMultiple) => onChange(newValue, index),
    [index, onChange],
  )
  const handleLabelChange = React.useCallback(
    (newLabel: string) => onChange({ label: newLabel }, index),
    [index, onChange],
  )
  const handleRemove = React.useCallback(() => onClose(index), [index, onClose])

  const actionOptions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType: type,
        partType: PartFamily.Voice,
        hasQuotas,
        actionNamespace: ActionContext.OnAnswer,
      }),
    [hasQuotas, type],
  )

  const value = React.useMemo(() => ({ ranges, replies }), [ranges, replies])

  return (
    <ActionsBox
      actionOptions={actionOptions}
      actions={actions}
      countriesTimezones={countriesTimezones}
      countryCode={countryCode}
      defaultLanguage={defaultLanguage}
      defaultSenderId={SMS_ACTION_DEFAULT_SENDERID.voiceQuestion}
      key={index}
      files={files}
      hasTranscribe={false}
      index={questionIndex}
      isCloseDisabled={isCloseDisabled}
      label={label}
      languages={languages}
      orgId={orgId}
      personalizationList={personalizationList}
      senderIds={senderIds}
      timezone={timezone}
      timezones={timezones}
      token={token}
      transcribeEnabled={false}
      updateContactConfig={{
        invalid: false,
        openResponse: false,
        question: true,
      }}
      voiceId={`${id}-${index}`}
      whatsappSenderIds={[]}
      onChange={handleActionsChange}
      onLabelChange={handleLabelChange}
      getImageProps={getImageProps}
      getVoiceProps={getVoiceProps}
      onClose={handleRemove}
    >
      <ChoiceTagMultiple
        data-testid={`voice-specific-choice-input-${index}`}
        value={value}
        onChange={handleChoiceChange}
      />
    </ActionsBox>
  )
}

export default React.memo(ResponseVoiceSpecificItem)
